import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../features.css";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { useBookData, resolveAgentName } from "../../data/BookDataProvider";
import { daysUntil, formatMoney } from "../../data/format";
import { Button, Card, DataTable, EmptyState, Select, BookIcon } from "../../ui";
import { LobBadge, RenewalBadge } from "../shared";

/**
 * Book of Business (§3.7): ONE household-grouped table for every tier —
 * the backend scopes the underlying lists (referral agents see their book,
 * staff see everything), so the page only adapts columns/filters per tier.
 * One row per household — never per policy. Row click → the client hub.
 */

const RENEWAL_WINDOWS = [
  { value: "", label: "Any renewal" },
  { value: "30", label: "Renews ≤ 30 days" },
  { value: "60", label: "Renews ≤ 60 days" },
  { value: "90", label: "Renews ≤ 90 days" }
];

const LOBS = [
  { value: "", label: "All lines" },
  { value: "auto", label: "Auto" },
  { value: "home", label: "Home" },
  { value: "umbrella", label: "Umbrella" }
];

function BookPage() {
  const navigate = useNavigate();
  const { tier, agents } = useAuth();
  const can = tierCan(tier);
  const { loading, error, households, refresh } = useBookData();

  // Deep-linkable initial filter: Today's "view all renewals" links to
  // /app/book?renews=60.
  const [searchParams] = useSearchParams();
  const [lobFilter, setLobFilter] = useState("");
  const [renewalFilter, setRenewalFilter] = useState(() => {
    const initial = searchParams.get("renews");
    return RENEWAL_WINDOWS.some((w) => w.value === initial) ? initial : "";
  });
  const [agentFilter, setAgentFilter] = useState("");

  const filtered = useMemo(() => {
    return households.filter((h) => {
      if (lobFilter && !h.activePolicies.some((p) => p.lobKey === lobFilter)) {
        return false;
      }
      if (renewalFilter) {
        if (!h.nextRenewal) {
          return false;
        }
        const days = daysUntil(h.nextRenewal.date);
        if (days == null || days > Number(renewalFilter)) {
          return false;
        }
      }
      if (agentFilter) {
        const attributed = [h.client.agentRef, h.client.agent1].filter(Boolean).map(String);
        if (!attributed.includes(agentFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [households, lobFilter, renewalFilter, agentFilter]);

  const columns = useMemo(() => {
    const cols = [
      {
        key: "household",
        header: "Household",
        sortValue: (h) => h.name.toLowerCase(),
        render: (h) => (
          <span className="feat-cell-stack">
            <span className="feat-cell-primary">{h.name}</span>
            {h.memberCount > 0 ? (
              <span className="feat-cell-secondary">
                {h.memberCount} household member{h.memberCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </span>
        )
      },
      {
        key: "contact",
        header: "Contact",
        sortable: false,
        render: (h) => (
          <span className="feat-cell-stack feat-cell-secondary">
            <span>{h.client.email || "—"}</span>
            <span>{h.client.telephone || ""}</span>
          </span>
        )
      },
      {
        key: "policies",
        header: "Policies",
        sortValue: (h) => h.activePolicies.length,
        render: (h) => {
          if (h.activePolicies.length === 0) {
            return <span className="fk-muted">None</span>;
          }
          const byLob = {};
          for (const p of h.activePolicies) {
            byLob[p.lobKey] = byLob[p.lobKey] || { lob: p.lob, count: 0 };
            byLob[p.lobKey].count += 1;
          }
          return (
            <span className="feat-badges">
              {Object.entries(byLob).map(([lobKey, { lob, count }]) => (
                <LobBadge key={lobKey} lobKey={lobKey} lob={lob} count={count} />
              ))}
            </span>
          );
        }
      },
      {
        key: "premium",
        header: "Premium",
        align: "num",
        sortValue: (h) => h.totalPremium,
        render: (h) => (h.totalPremium > 0 ? formatMoney(h.totalPremium) : "—")
      },
      {
        key: "renewal",
        header: "Next renewal",
        sortValue: (h) => (h.nextRenewal ? h.nextRenewal.date.getTime() : null),
        render: (h) => <RenewalBadge renewal={h.nextRenewal} />
      }
    ];
    if (can.seeAgentAttribution) {
      cols.push({
        key: "agent",
        header: "Agent",
        sortValue: (h) =>
          (resolveAgentName(agents, h.client.agentRef || h.client.agent1) || "zzz").toLowerCase(),
        render: (h) => {
          const name = resolveAgentName(agents, h.client.agentRef || h.client.agent1);
          return name || <span className="fk-muted">Unassigned</span>;
        }
      });
    }
    return cols;
  }, [can.seeAgentAttribution, agents]);

  if (error) {
    return (
      <div className="shell-page">
        <div className="shell-page__titlerow">
          <h1 className="shell-page__title">Book of Business</h1>
        </div>
        <Card>
          <EmptyState
            icon={<BookIcon size={32} />}
            title="Something went wrong"
            message={error}
            action={
              <Button variant="primary" onClick={refresh}>
                Try again
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">
          {can.seeAgentAttribution ? "Book of Business" : "My book"}
        </h1>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(h) => h.id}
        onRowClick={(h) => navigate(`/app/clients/${h.id}`)}
        loading={loading}
        countLabel="households"
        searchPlaceholder="Search name, email, phone, address, policy #…"
        searchText={(h) =>
          [
            h.name,
            h.client.firstName,
            h.client.lastName,
            h.client.email,
            h.client.telephone,
            h.client.address,
            // Coordinator (Evan): find a household by any of its policy numbers
            // — incl. inactive ones (h.policies, not activePolicies), so e.g.
            // "5066" surfaces the Deborah Munz household. Surfaces the household
            // row as usual; no new UI/route.
            ...h.policies.map((p) => p.policyNumber)
          ]
            .filter(Boolean)
            .join(" ")
        }
        toolbarExtra={
          can.seeAgentAttribution ? (
            <>
              <Select
                className="feat-filter"
                aria-label="Filter by line of business"
                value={lobFilter}
                onChange={(e) => setLobFilter(e.target.value)}
              >
                {LOBS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Select
                className="feat-filter"
                aria-label="Filter by renewal window"
                value={renewalFilter}
                onChange={(e) => setRenewalFilter(e.target.value)}
              >
                {RENEWAL_WINDOWS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <Select
                className="feat-filter"
                aria-label="Filter by agent"
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <option value="">All agents</option>
                {(agents || []).map((a) => (
                  <option key={a._id} value={String(a._id)}>
                    {[a.firstName, a.lastName].filter(Boolean).join(" ") || a.email}
                  </option>
                ))}
              </Select>
            </>
          ) : (
            /* Referral toolbar (E §5): search + one "Renewing soon" chip. */
            <button
              type="button"
              className={`fk-chip${renewalFilter ? " fk-chip--on" : ""}`}
              style={{ minHeight: 32 }}
              aria-pressed={Boolean(renewalFilter)}
              onClick={() => setRenewalFilter(renewalFilter ? "" : "60")}
            >
              Renewing soon
            </button>
          )
        }
        emptyState={
          <EmptyState
            icon={<BookIcon size={32} />}
            title="No households found"
            message="No clients match the current search and filters — try clearing them, or check the spelling."
          />
        }
      />
    </div>
  );
}

export default BookPage;
